import React, { useMemo } from 'react';
import {
  TextField, Button, List, ListItem, ListItemText, Paper, Container, Typography,
  CircularProgress, Avatar, ListItemAvatar, Divider, Box, Skeleton, InputAdornment, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { useParams } from 'react-router-dom';
import DJInfoSection from '../components/DJForm/DJInfoSection';
import SelectedSongSection from '../components/DJForm/SelectedSongSection';
import FooterLogo from '../components/FooterLogo';
import SongRequestModal from '../components/DJForm/SongRequestModal';
import { motion, AnimatePresence } from 'framer-motion';
import { useSongRequest } from '../hooks/useSongRequest';

const MemoizedDJInfoSection = React.memo(DJInfoSection);
const MemoizedSelectedSongSection = React.memo(SelectedSongSection);
const MemoizedFooterLogo = React.memo(FooterLogo);
const MemoizedSongRequestModal = React.memo(SongRequestModal);

const SongRequestForm = () => {
  const { formId } = useParams();
  const {
    query,
    setQuery,
    tableNumber,
    tableNumberError,
    results,
    selectedSong,
    open,
    loading,
    error,
    djInfo,
    loadingDJInfo,
    handleSearch,
    handleSelect,
    handleTableNumberChange,
    handleSubmit,
    handleCancel,
    handleClose
  } = useSongRequest(formId);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const resultsMemo = useMemo(() => results, [results]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const listVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        background: `linear-gradient(rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.95)), url("https://images.unsplash.com/photo-1514525253440-b393452e8d26?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 2, md: 2 }, // Reduced padding on desktop
        px: { xs: 1, md: 2 }
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '550px' }}> {/* Compact width */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <Paper
            elevation={24}
            sx={{
              padding: { xs: 2, md: 3 }, // Reduced padding on desktop
              borderRadius: { xs: '16px', md: '24px' },
              background: 'rgba(20, 20, 20, 0.75)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
              color: '#fff',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Decorative top gradient line */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #d50000, #ff1744, #d50000)'
              }}
            />

            <MemoizedDJInfoSection djInfo={djInfo} loadingDJInfo={loadingDJInfo} />

            {loadingDJInfo ? (
              <Box sx={{ mt: 2 }}>
                <Skeleton variant="text" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
                <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.05)' }} />
                <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.05)' }} />
              </Box>
            ) : !djInfo['Cuenta Activa'] ? (
              <Box
                sx={{
                  mt: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'rgba(213, 0, 0, 0.08)',
                  border: '1px solid rgba(213, 0, 0, 0.3)',
                  borderRadius: 3,
                  color: '#ff5252'
                }}
              >
                <Typography variant="h6" component="div" fontWeight="bold" sx={{ mb: 1 }}>
                  Servicio no disponible
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Este formulario está temporalmente desactivado.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}> {/* Reduced margin top */}
                {!selectedSong ? (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key="search-form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                          mb: 2, // Reduced margin bottom
                          textAlign: 'center',
                          fontWeight: 700,
                          color: '#fff',
                          letterSpacing: '0.5px',
                          fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.5rem' }, // Slightly smaller on desktop
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        ¿Qué quieres escuchar hoy?
                      </Typography>

                      {formId === 'fm0924tom' && (
                        <TextField
                          fullWidth
                          label="Nº de mesa"
                          value={tableNumber}
                          onChange={handleTableNumberChange}
                          variant="outlined"
                          size="small" // Smaller input
                          sx={{
                            mb: 2, // Reduced margin
                            "& .MuiOutlinedInput-root": {
                              color: "#fff",
                              borderRadius: '16px',
                              backgroundColor: 'rgba(255,255,255,0.03)',
                              transition: 'all 0.3s ease',
                              "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                              "&:hover": { backgroundColor: 'rgba(255,255,255,0.06)' },
                              "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                              "&.Mui-focused": { backgroundColor: 'rgba(255,255,255,0.08)' },
                              "&.Mui-focused fieldset": { borderColor: "#d50000", borderWidth: '2px' },
                            },
                            "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#d50000" },
                          }}
                          error={!!tableNumberError}
                          helperText={tableNumberError}
                        />
                      )}

                      <TextField
                        fullWidth
                        label="Buscar canción o artista"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                        size="small" // Smaller input
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={handleSearch} sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}>
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 3, // Reduced margin
                          "& .MuiOutlinedInput-root": {
                            color: "#fff",
                            borderRadius: '16px',
                            backgroundColor: 'rgba(255,255,255,0.03)',
                            transition: 'all 0.3s ease',
                            "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                            "&:hover": { backgroundColor: 'rgba(255,255,255,0.06)' },
                            "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                            "&.Mui-focused": { backgroundColor: 'rgba(255,255,255,0.08)' },
                            "&.Mui-focused fieldset": { borderColor: "#d50000", borderWidth: '2px' },
                          },
                          "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
                          "& .MuiInputLabel-root.Mui-focused": { color: "#d50000" },
                        }}
                        error={!!error}
                        helperText={error}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        size="medium" // Smaller button
                        onClick={handleSearch}
                        disabled={loading}
                        sx={{
                          mb: 3, // Reduced margin
                          py: 1.5, // Reduced padding
                          borderRadius: '50px',
                          background: 'linear-gradient(135deg, #d50000 0%, #b71c1c 100%)',
                          boxShadow: '0 10px 30px rgba(213, 0, 0, 0.3)',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          fontSize: '0.9rem', // Smaller font
                          fontWeight: 800,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 15px 35px rgba(213, 0, 0, 0.5)',
                            background: 'linear-gradient(135deg, #ff1744 0%, #d50000 100%)',
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          }
                        }}
                      >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'BUSCAR CANCIÓN'}
                      </Button>

                      {loading && (
                        <Box sx={{ mt: 2 }}>
                          {[1, 2, 3].map((i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Skeleton variant="circular" width={50} height={50} sx={{ bgcolor: 'rgba(255,255,255,0.05)', mr: 2 }} />
                              <Box sx={{ width: '100%' }}>
                                <Skeleton variant="text" width="70%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 0.5 }} />
                                <Skeleton variant="text" width="40%" height={16} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}

                      <List component={motion.ul} variants={listVariants} initial="hidden" animate="visible" sx={{ p: 0 }}>
                        <AnimatePresence>
                          {resultsMemo.map((song, index) => (
                            <motion.li key={index} variants={itemVariants} layout style={{ marginBottom: '8px' }}>
                              <ListItem
                                onClick={() => handleSelect(song)}
                                sx={{
                                  cursor: 'pointer',
                                  borderRadius: '12px',
                                  p: 1.5,
                                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                  border: '1px solid rgba(255, 255, 255, 0.05)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    borderColor: 'rgba(255, 255, 255, 0.1)',
                                    transform: 'scale(1.01)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                                  },
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    src={song.album.cover}
                                    alt={song.title}
                                    variant="rounded"
                                    sx={{ width: 56, height: 56, borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
                                  >
                                    <MusicNoteIcon />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', mb: 0.5, lineHeight: 1.2, fontSize: '0.95rem' }}>
                                      {song.title}
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500, fontSize: '0.85rem' }}>
                                      {song.artist.name}
                                    </Typography>
                                  }
                                  sx={{ ml: 2 }}
                                />
                                <IconButton sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#d50000' } }}>
                                  <MusicNoteIcon />
                                </IconButton>
                              </ListItem>
                            </motion.li>
                          ))}
                        </AnimatePresence>
                      </List>
                    </motion.div>
                  </AnimatePresence>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, type: 'spring' }}
                  >
                    <MemoizedSelectedSongSection
                      selectedSong={selectedSong}
                      handleCancel={handleCancel}
                      handleSubmit={handleSubmit}
                      loading={loading}
                    />
                  </motion.div>
                )}
              </Box>
            )}

            <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <MemoizedFooterLogo djInfo={djInfo} />
            </Box>
          </Paper>
        </motion.div>

        {djInfo['Cuenta Activa'] && (
          <MemoizedSongRequestModal
            open={open}
            handleClose={handleClose}
            djInfo={djInfo}
            tableNumber={formId === 'fm0924tom' ? tableNumber : undefined}
          />
        )}
      </Box>
    </Box>
  );
};

export default SongRequestForm;
